/** Open invoices of one customer at document level (`GET credit-limits/{code}/open-invoices`, A2). */
import { useEffect, useState } from 'react';
import { creditLimitsApi } from '../../../services/creditLimitsApi';
import type { OpenInvoicesResponse, OverviewRow } from '../../../types/creditLimits';
import { OverviewDialog } from './OverviewDialog';
import { formatChf } from './overviewColumns';

function formatDate(value: string | null): string {
    if (!value) return '–';
    const [y, m, d] = value.slice(0, 10).split('-');
    return y && m && d ? `${d}.${m}.${y}` : value;
}

export function OpenInvoicesDialog({ row, onClose }: { row: OverviewRow; onClose: () => void }) {
    const [data, setData] = useState<OpenInvoicesResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        creditLimitsApi.getOpenInvoices(row.customerCode)
            .then(d => { if (!cancelled) setData(d); })
            .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load open invoices'); });
        return () => { cancelled = true; };
    }, [row.customerCode]);

    return (
        <OverviewDialog title="Open invoices" subtitle={`${row.customerName ?? row.customerCode} (${row.customerCode}) · amounts in CHF`} onClose={onClose}>
            {error && <div className="banner error" role="alert"><p>{error}</p></div>}
            {!error && !data && <p className="muted" role="status">Loading…</p>}
            {data && data.items.length === 0 && <p className="muted">No open invoices.</p>}
            {data && data.items.length > 0 && (
                <div className="tbl-wrap">
                    <table className="tbl">
                        <caption className="sr-only">Open invoices of {row.customerName ?? row.customerCode}</caption>
                        <thead>
                            <tr>
                                <th scope="col">Document</th>
                                <th scope="col">Type</th>
                                <th scope="col">Posted</th>
                                <th scope="col">Due</th>
                                <th scope="col" className="n">Days past due</th>
                                <th scope="col" className="n">Remaining CHF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map(i => (
                                <tr key={i.entryNo}>
                                    <td className="key">{i.documentNr ?? '–'}</td>
                                    <td>{i.documentType || '–'}</td>
                                    <td>{formatDate(i.postingDate)}</td>
                                    <td>{formatDate(i.dueDate)}</td>
                                    <td className="n">{i.daysOverdue === null ? '–' : i.daysOverdue > 0 ? i.daysOverdue : '–'}</td>
                                    <td className="n">{formatChf(i.restChf)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={5}>Total ({data.items.length})</td>
                                <td className="n">{formatChf(data.totalRestChf)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </OverviewDialog>
    );
}
